/**
 * Users API
 * Manages workspace users
 */

import { BaseApiClient } from "./base";

// ========== Types ==========

export type UserRole = "user" | "admin" | "owner";

export interface UserIdentity {
  id: string;
  external_system: string;
  external_id: string;
  external_name: string | null;
}

export interface User {
  id: string;
  workspace_id: string;
  email: string;
  firstname: string | null;
  lastname: string | null;
  role: UserRole;
  identities: UserIdentity[];
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  email: string;
  firstname?: string;
  lastname?: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  email?: string;
  firstname?: string;
  lastname?: string;
  role?: UserRole;
}

export interface UserExternalIdentity {
  id: string;
  user_id: string;
  external_system: string;
  external_id: string;
  external_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateUserIdentityRequest {
  external_system: string;
  external_id: string;
  external_name?: string;
}

// ========== API Client ==========

class UsersApiClient extends BaseApiClient {
  /**
   * List all users in the workspace
   */
  async list(): Promise<User[]> {
    return this.request<User[]>("/users");
  }

  /**
   * Get a specific user by ID
   */
  async get(id: string): Promise<User> {
    return this.request<User>(`/users/${id}`);
  }

  /**
   * Create a new user
   */
  async create(data: CreateUserRequest): Promise<User> {
    return this.request<User>("/users", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  /**
   * Update a user
   */
  async update(id: string, data: UpdateUserRequest): Promise<User> {
    return this.request<User>(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete a user
   */
  async delete(id: string): Promise<{ success: boolean; id: string }> {
    return this.request<{ success: boolean; id: string }>(`/users/${id}`, {
      method: "DELETE"
    });
  }

  /**
   * List user external identities
   */
  async listIdentities(userId: string): Promise<UserExternalIdentity[]> {
    return this.request<UserExternalIdentity[]>(`/users/${userId}/identities`);
  }

  /**
   * Link external identity to user
   */
  async createIdentity(
    userId: string,
    data: CreateUserIdentityRequest
  ): Promise<UserExternalIdentity> {
    return this.request<UserExternalIdentity>(`/users/${userId}/identities`, {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  /**
   * Unlink external identity from user
   */
  async deleteIdentity(userId: string, identityId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/users/${userId}/identities/${identityId}`, {
      method: "DELETE"
    });
  }
}

export const usersApi = new UsersApiClient();
